import './meat-agent-reply'
import type { AgentReply } from './meat-agent-reply'

const template = document.createElement('template')
template.innerHTML = `
  <style>
    :host {
      display: flex;
      flex-direction: column;
      min-width: 0;
      min-height: 0;
      border-radius: var(--radius-md);
      background: var(--color-surface);
      color: var(--color-text);
      overflow: hidden;
    }
    header {
      display: flex;
      align-items: center;
      gap: var(--space-2);
      padding: var(--space-2-5) var(--space-3);
      background: var(--color-surface-header);
      font-size: var(--font-size-sm);
      font-weight: var(--font-weight-bold);
    }
    .label {
      color: var(--color-text-muted);
      font-weight: normal;
    }
    button {
      /* It's taller than the title, but shouldn't make the header any taller. */
      margin: calc(-1 * var(--space-1)) 0 calc(-1 * var(--space-1)) auto;
      padding: 0 var(--space-1-5);
      border: none;
      border-radius: var(--radius-sm);
      background: none;
      color: var(--color-text-secondary);
      font: inherit;
      font-size: 1.25em;
      line-height: 1.5;
      cursor: pointer;
    }
    button:hover {
      background: var(--color-surface-footer);
    }
    main {
      flex: 1;
      padding: var(--space-4);
      overflow-y: auto;
      overflow-wrap: anywhere;
      line-height: var(--line-height);
    }
    .unsupported {
      color: var(--color-text-muted);
      font-style: italic;
    }
  </style>
  <header>
    <span class="label"></span>
    <span class="title"></span>
    <button type="button" title="Close" aria-label="Close">×</button>
  </header>
  <main></main>
`

/**
 * Shows one of an agent's windows: a widget under a title, rendered according
 * to its content type. The `label` attribute names the agent the window is
 * from. Its close button fires a `close` event, leaving the room to close it
 * for everyone.
 */
export class WindowCard extends HTMLElement {
  #root: ShadowRoot
  #label: HTMLElement
  #title: HTMLElement
  #body: HTMLElement
  // The markdown on show, kept so new content fades in what's changed.
  #markdown?: AgentReply

  static observedAttributes = ['label']

  constructor() {
    super()
    this.#root = this.attachShadow({ mode: 'open' })
    this.#root.append(template.content.cloneNode(true))
    this.#label = this.#root.querySelector('.label')!
    this.#title = this.#root.querySelector('.title')!
    this.#body = this.#root.querySelector('main')!
    this.#root.querySelector('button')!.addEventListener('click', () => {
      this.dispatchEvent(new Event('close'))
    })
  }

  attributeChangedCallback(name: string, _old: string | null, value: string | null) {
    if (name === 'label') this.#label.textContent = value ?? ''
  }

  /** Shows the window's latest title and widget. */
  show(title: string, contentType: string, content: Uint8Array) {
    this.#title.textContent = title
    // Parameters like charset are ignored, since text is always UTF-8.
    switch (contentType.replace(/;.*/s, '').trim().toLowerCase()) {
      case 'text/markdown':
        this.#showMarkdown(new TextDecoder().decode(content))
        break
      default:
        this.#showUnsupported(contentType)
    }
  }

  #showMarkdown(text: string) {
    if (!this.#markdown) {
      this.#markdown = document.createElement('meat-agent-reply')
      this.#body.replaceChildren(this.#markdown)
    }
    this.#markdown.setText(text)
  }

  #showUnsupported(contentType: string) {
    this.#markdown = undefined
    const note = document.createElement('p')
    note.className = 'unsupported'
    note.textContent = `Can't show ${contentType || 'content without a type'}.`
    this.#body.replaceChildren(note)
  }
}

customElements.define('meat-window-card', WindowCard)

declare global {
  interface HTMLElementTagNameMap {
    'meat-window-card': WindowCard
  }
}
